import { useMemo, Fragment } from 'react'
import cx from 'classnames'
import { isNil } from 'lodash'

import { MAX_IMAGE_POST, MAX_IMAGE_SHARED_POST } from 'config'
import { useModalState } from 'hooks'

import ImageLoader from 'components/ImageLoader'
import LightBox from 'components/LightBox'

import './style.scss'

const FALLBACK_MAX_HEIGHT = 800

// More information about the specs and implementation can be found at:
// - https://wiki.nanameue.jp/display/YAYAPP/YAY-5179+When+user+upload+image%2C+show+width+and+height+accurate+ratio+on+timeline+cell
// - https://github.com/nanameue/yay_web/pull/600

const PostImage = ({
  post,
  isShared,
  forceMultiple = false,
}: {
  post: any
  isShared?: boolean
  forceMultiple?: boolean
}) => {
  const lightBox = useModalState()

  const maxImage = useMemo(() => {
    if (isShared) {
      if (post.text) {
        return 1
      } else {
        return MAX_IMAGE_SHARED_POST
      }
    }

    return MAX_IMAGE_POST
  }, [isShared])

  const images = useMemo(() => {
    return Array.from(Array(maxImage))
      .map((_, i) => ({
        attachment: post?.[i === 0 ? 'attachment' : `attachment_${i + 1}`],
        attachment_thumbnail:
          post?.[
            i === 0 ? 'attachment_thumbnail' : `attachment_${i + 1}_thumbnail`
          ],
      }))
      .filter(
        ({ attachment, attachment_thumbnail }) =>
          attachment ?? attachment_thumbnail
      )
  }, [post, isShared])

  const extractImageSize = (imageFileName: string) => {
    // eg. https://cdn.yay.space/file/yay-space-stg/uploads/post/22089/images/1_size_3000x4000.jpeg [3000x4000, 3000, 4000]
    const _imageSize = (imageFileName ?? '').match(/([0-9]+)x([0-9]+)/)

    if (_imageSize === null) return null

    return {
      width: Number(_imageSize[1] ?? 0),
      height: Number(_imageSize[2] ?? 0),
    }
  }

  const isSingleImage = images.length === 1 && !forceMultiple
  const imageSize = extractImageSize(post?.attachment ?? '')
  const isSizesAvailable = imageSize?.width > 0 && imageSize?.height > 0

  if (!post?.attachment_thumbnail && !post?.attachment) {
    return null
  }

  return (
    <Fragment>
      <div
        className={cx('PostImage', {
          'PostImage--1': isSingleImage,
        })}
      >
        {images.map((image, i) => {
          const imageRatio =
            imageSize?.width > 0 && imageSize?.height > 0
              ? imageSize?.width / imageSize?.height
              : 1

          const isTooNarrowTall = imageRatio < 3 / 4
          const isTooWideShort = imageRatio > 2 / 1

          return (
            <div
              className={cx('PostImage__wrapper', {
                'PostImage__wrapper--tall': isTooNarrowTall && isSingleImage,
              })}
              key={i}
            >
              {isSingleImage ? (
                <Fragment>
                  {isSizesAvailable && (
                    <Fragment>
                      {(() => {
                        let _width = imageSize?.width
                        let _height = imageSize?.height

                        if (isTooNarrowTall) {
                          // An image may not be too narrow and tall.
                          _width = 3
                          _height = 4
                        } else if (isTooWideShort) {
                          // An image may not be too wide and short.
                          _width = 2
                          _height = 1
                        }

                        // In case of a single image which `imageSize?.width` and `imageSize?.height` are available,
                        // We'll use an svg to control its aspect ratio (given `aspect-ratio`
                        // doesn't play well with our specs, and is not supported in older brower).
                        //
                        // The case when we won't use an svg is when either `imageSize?.width` or `imageSize?.height`
                        // is not available, making it not possible to determine an aspect ratio.
                        // In this case we'll have the image render naturally (without `.PostImage__img--absolute`)
                        // with a maximum height of `FALLBACK_MAX_HEIGHT`.
                        return (
                          <svg
                            className="PostImage__a__svg"
                            viewBox={`0 0 ${_width} ${_height}`}
                          />
                        )
                      })()}
                    </Fragment>
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  {(() => {
                    // The case for multiple images is straightforward, we'll just render the images
                    // as square ones. No need to worry about aspect ratios at all.
                    return <svg className="PostImage__svg" viewBox="0 0 1 1" />
                  })()}
                </Fragment>
              )}

              <ImageLoader
                className={cx('PostImage__img', {
                  'PostImage__img--absolute':
                    !isSingleImage || isSizesAvailable,
                })}
                url={
                  isSingleImage
                    ? image.attachment ?? image.attachment_thumbnail // Show big image.
                    : image.attachment_thumbnail ?? image.attachment // Show thumbnail image.
                }
                onClick={() =>
                  lightBox.open(image.attachment ?? image.attachment_thumbnail)
                }
                style={{
                  ...(isSingleImage && isNil(imageSize)
                    ? {
                        maxHeight: `${FALLBACK_MAX_HEIGHT}px`,
                      }
                    : {}),
                }}
              />
            </div>
          )
        })}
      </div>

      <LightBox
        isActive={lightBox.isActive}
        onClose={lightBox.close}
        url={lightBox.data}
      />
    </Fragment>
  )
}

export default PostImage
